<template>
  <pdl-collapse-item
    :key="getCollapseItemName(partTypeKey, itemIdKey, itemIndex)"
    :name="getCollapseItemName(partTypeKey, itemIdKey, itemIndex)"
  >
    <div
      v-if="partTypeKey === 'customWaterBottles'"
      slot="title"
      class="heading heading--sm"
      qaid="custom-water-bottle-heading"
    >
      <span v-html="getBaseProperty('customShippingCost')" />
    </div>
    <div v-else-if="!isProjectOne" slot="title" class="heading heading--sm" qaid="distributionCenter.heading">
      {{ warehouse.name }} ({{ warehouse.count }})
    </div>

    <!-- Callout for Current Availability -->
    <pdl-callout
      v-if="hasBackorders && !isCustomWaterBottle && !isProjectOne"
      kind="warning"
      qaid="distributionCenter.backorders.warning"
    >
      <template #content>
        <span v-html="getBaseProperty('primaryWarehouse')" />
      </template>
    </pdl-callout>

    <div v-if="showShippingInfo" class="mt-2 mb-5" qaid="distributionCenter.shipOptions">
      <fieldset class="form-fieldset">
        <legend class="form-fieldset__legend text-md">{{ getBaseProperty('shipOrBackorderPrimary') }}</legend>
        <label :for="getInputId('ship')" class="radio text-md">
          <input :id="getInputId('ship')" v-model="warehouse.isBackOrdered" type="radio" :value="false" />
          <span class="control-indicator"></span>
          {{ getBaseProperty('shipLines') }}
        </label>
        <label :for="getInputId('backorder')" class="radio text-md">
          <input :id="getInputId('backorder')" v-model="warehouse.isBackOrdered" type="radio" :value="true" />
          <span class="control-indicator"></span>
          {{ getBaseProperty('backorderInPrimary') }}
        </label>
      </fieldset>
    </div>
    <pdl-tabs>
      <!-- SKU Tab -->
      <pdl-tab-pane :label="bp('sku')">
        <!-- SKU GRID -->
        <table class="b2b-grid b2b-grid--collapse is-compact" qaid="saved-list-sku-grid">
          <!-- SKU Grid Headers -->
          <thead class="b2b-grid__header">
            <tr>
              <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.icon"></th>
              <th
                v-if="b2bProductPriority"
                class="b2b-grid__cell b2b-grid__head"
                qaid="sku-grid.sold"
                v-html="bp('sold')"
              />
              <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.note"></th>
              <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.split"></th>

              <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.sku">{{ getBaseProperty('sku') }}</th>

              <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.item">{{ getBaseProperty('item') }}</th>

              <th v-if="!isProjectOne" class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.upc-ean">
                {{ getBaseProperty('upc') }}
              </th>

              <th
                v-if="isCustomWaterBottle || isProjectOne"
                class="b2b-grid__cell b2b-grid__head lg:text-right"
                qaid="sku-grid-custom-id"
              >
                <span v-html="bp('customId')" />
              </th>
              <template v-else>
                <th
                  v-if="checkoutStep != 'confirmation'"
                  class="b2b-grid__cell b2b-grid__head lg:text-right"
                  qaid="sku-grid.allocated"
                >
                  <span
                    v-tippy="{
                      html: '#abbreviation-text',
                      trigger: 'mouseenter',
                    }"
                    v-html="getBaseProperty('alloc')"
                  >
                  </span>
                </th>

                <th
                  v-if="checkoutStep != 'confirmation' && !isProjectOne"
                  class="b2b-grid__cell b2b-grid__head lg:text-right"
                  qaid="sku-grid.backordered"
                >
                  <span
                    v-tippy="{
                      html: '#bo-abbreviation-text',
                      trigger: 'mouseenter',
                    }"
                    v-html="getBaseProperty('bo')"
                  >
                  </span>
                </th>
              </template>
              <th
                v-if="!consumerFriendlyMode && !isProjectOne"
                class="b2b-grid__cell b2b-grid__head"
                qaid="sku-grid.order-price"
              >
                {{ getBaseProperty('price') }}
              </th>

              <th
                v-if="checkoutStep != 'confirmation' && !isProjectOne"
                qaid="sku-grid.list "
                class="b2b-grid__cell b2b-grid__head lg:text-right"
              >
                <i class="md-18">list</i>
              </th>

              <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.qty">{{ getBaseProperty('qty') }}</th>

              <th
                v-if="!consumerFriendlyMode"
                class="b2b-grid__cell b2b-grid__head text-right"
                qaid="sku-grid.subtotal"
              >
                {{ getBaseProperty('subtotal') }}
              </th>
            </tr>
          </thead>
          <span id="abbreviation-text" class="hidden" v-html="bp('allocated')"> </span>
          <span id="bo-abbreviation-text" class="hidden" v-html="bp('backordered')"> </span>
          <!-- SKU Grid Body/Rows -->
          <tbody>
            <!-- For each Product/Row -->
            <checkout-grid-row
              v-for="(item, index) in paginatedData"
              :key="item[itemIdKey] + '_' + index"
              :item="item"
              :is-backorder="warehouse.id === 'backOrdered'"
              :headers="tableHeaders"
              :checkout-step="checkoutStep"
              :base-properties="baseProperties"
              :part-type-key="partTypeKey"
              :cart-id="cartId"
              :is-custom-water-bottle="isCustomWaterBottle"
              :is-project-one="isProjectOne"
              :b2b-product-priority="b2bProductPriority"
              @update-item-bto-data="updateItemBTOData"
            />
          </tbody>
        </table>

        <div class="grid-x grid-margin-x mb-5">
          <div class="cell medium-6 large-8">
            <pagination
              v-if="pageCount > 1"
              ref="pagination"
              :page-number="pageNumber"
              :page-count="pageCount"
              @page-change="pageChange"
              @prev-page="prevPage"
              @next-page="nextPage"
            ></pagination>
          </div>
          <div v-if="!consumerFriendlyMode" class="cell medium-6 large-4">
            <!-- Shipment Summary -->
            <ul class="block-list text-md">
              <li class="block-list__item" qaid="shipmentSummary.subtotal">
                <span>{{ getBaseProperty('subtotal') }}</span>
                <span>{{ warehouse.subTotal }}</span>
              </li>

              <li
                v-if="!hasBackorders && !isDistributor"
                class="block-list__item"
                qaid="shipmentSummary.estimatedShipping"
              >
                <span>{{ getBaseProperty('estimatedShipping') }}</span>
                <span>{{ warehouse.formattedEstimatedShipping }}</span>
              </li>
              <li class="block-list__item" qaid="shipmentSummary.paymentTerms">
                <span>{{ getBaseProperty('paymentTerms') }}</span>
                <span>{{ warehouse.paymentTerms }}</span>
              </li>
              <li class="block-list__item text-base font-medium" qaid="shipmentSummary.total">
                <span>{{ getBaseProperty('warehouseTotal') }}</span>
                <span>{{ warehouse.total }}</span>
              </li>
            </ul>
          </div>
        </div>
      </pdl-tab-pane>
      <pdl-tab-pane v-if="displayAppleLabels" :label="bp('appleLabelTitle')">
        <pdl-callout v-if="!cartToggle" kind="error">
          <template #content>
            <span v-html="bp('cannotSubmit')" />
          </template>
        </pdl-callout>

        <apple-label-grid
          :key="warehouse.id"
          :columns="appleTableHeaders"
          :items="appleLabelData"
          :base-properties="baseProperties"
          :text-save-button="bp('textSaveButton')"
          :text-clear-button="bp('textClearButton')"
          :text-cannot-save="bp('textCannotSave')"
        ></apple-label-grid>
      </pdl-tab-pane>
    </pdl-tabs>
  </pdl-collapse-item>
</template>

<script>
import pagination from '../../mixins/pagination';
import find from 'lodash/find';
import {mapState, mapGetters} from 'vuex';
import {PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import CheckoutGridRow from '@/components/checkout/CheckoutGridRow';
import Pagination from '@/components/Pagination';
import AppleLabelGrid from '@/components/checkout/AppleLabelGrid';
import {PdlCallout} from '@pedal/pdl-callout';
import {formatAppleData} from '@/utils/product-info';

export default {
  components: {PdlCollapseItem, PdlTabs, PdlTabPane, CheckoutGridRow, Pagination, AppleLabelGrid, PdlCallout},

  mixins: [pagination],

  props: {
    hasShippingOptions: {
      type: Boolean,
      default: false,
    },
    itemIdKey: {
      type: String,
      default: '',
    },
    partTypeKey: {
      type: String,
      default: '',
    },
    itemIndex: {
      type: Number,
      default: 0,
    },
    size: {
      type: Number,
      required: false,
      default: ACC.checkoutItemsPageSize || 50,
    },
    type: {
      type: String,
      default: '',
    },
    warehouse: {
      type: Object,
      default: () => {},
    },
    tableHeaders: {
      type: Array,
      default: () => [],
      required: false,
    },
    baseProperties: {
      type: Array,
      default: () => [],
      required: false,
    },
    appleTableHeaders: {
      type: Array,
      default: () => [],
      required: false,
    },
    checkoutStep: {
      type: String,
      default: '',
    },
    b2bProductPriority: {
      type: Boolean,
      default: false,
      required: false,
    },
    cartId: {
      type: String,
      default: '',
      required: false,
    },
  },

  computed: {
    hasBackorders() {
      return this.warehouse.id === 'backOrdered';
    },

    displayAppleLabels() {
      if (this.partTypeKey === 'bikes') {
        return this.appleLabelAccess.bikes;
      }
      if (this.partTypeKey === 'parts') {
        return this.appleLabelAccess.parts;
      }

      return false;
    },

    appleLabelData() {
      if (!this.displayAppleLabels) return [];
      if (!this.warehouse.entries) return [];
      let appleLabelPaginatedData = this.paginatedData;
      return formatAppleData(appleLabelPaginatedData);
    },

    showShippingInfo() {
      return this.hasShippingOptions && !this.warehouse.isPrimary && !this.hasBackorders;
    },

    listData() {
      if (this.warehouse == null || this.warehouse.entries == null) return [];
      return this.warehouse.entries;
    },

    isCustomWaterBottle() {
      return this.partTypeKey === 'customWaterBottles';
    },
    isProjectOne() {
      return this.partTypeKey === 'projectOneBikes';
    },
    ...mapState('backend', ['isDistributor', 'cartToggle', 'appleLabelAccess']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  methods: {
    getBaseProperty(name) {
      let baseProperty = find(this.baseProperties, (baseProperty) => baseProperty.name === name);
      return baseProperty ? baseProperty.label : '';
    },

    getCollapseItemName(partTypeKey, itemIdKey, index) {
      return `${partTypeKey}-${itemIdKey}-${index}`;
    },

    getInputId(suffix) {
      return `${this.partTypeKey}-${this.warehouse.id}-${suffix}`;
    },

    updateItemBTOData(payload) {
      payload.warehouseId = this.warehouse.id;
      this.$emit('update-item-bto-data', payload);
    },
  },
};
</script>
